// Headlight `RichText` input provider — the markdown editor from pict-section-form (edit mode) paired
// with rendered-markdown view mode, wrapped with the shared inline View/Edit presentation
// behavior (see EditorBase). PSF does not auto-register this provider (its editor dep is an opt-in), so
// the Headlight bundle registers it under the hash `Pict-Input-RichText` that the templates'
// DefaultInputExtensions reference.
const RichTextInput = require('pict-section-form').RichTextInput;
const { buildEditorInputProvider } = require('./PictProvider-InputType-EditorBase.js');
const libUserMarkdown = require('./_UserMarkdown.js');
const libTestResultsSummary = require('./_TestResultsSummary.js');

// The markdown editor builds its CodeMirror surface from `window.CodeMirrorModules` and has no loader
// of its own — without it it renders an EMPTY slot and throws nothing, which reads as "there is no way
// to edit". The bundle is ~1.1MB, so rather than script-tag it into every document we fetch it on the
// first edit (the same lazy shape the Diagram editor uses). Override the URL per app via provider
// options; set it to null to opt out entirely when a host already loads CodeMirror.
const _DEFAULT_CODEMIRROR_URL = './codemirror-bundle.js';

/** The markdown editor's CodeMirror globals are set by the vendor bundle, so they are not on the Window type. */
const _codeMirrorLoaded = () => !!(/** @type {any} */ (globalThis).CodeMirrorModules);

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'Pict-Input-RichText',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,
	// Join the app save-data lifecycle so EditorBase.onBeforeSaveDataAsync fires — it flushes an editor
	// left open at save time into FormData (the per-keystroke marshal is deferred to View, so without
	// this a Save-while-editing would persist the pre-edit value). NOT a framework default.
	AutoSaveDataWithApp: true,

	CodeMirrorVendorURL: _DEFAULT_CODEMIRROR_URL,
};

const EditorProviderBase = buildEditorInputProvider(RichTextInput, _DEFAULT_CONFIGURATION);

class PictProviderInputTypeRichText extends EditorProviderBase
{
	/**
	 * A RichText value is text a user typed (a test remark), so its View / Read / Print rendering goes
	 * through the hardened renderer, the same one LIMS and the sample panel use. PSF's own uses a plain
	 * `marked.parse`, which puts typed HTML (an `onerror` attribute, a `javascript:` link) into the
	 * page of whoever reads the remark.
	 *
	 * @param {string} pMarkdown - the stored remark.
	 * @return {string} - the rendered HTML.
	 */
	_renderMarkdown(pMarkdown)
	{
		return libUserMarkdown.renderUserMarkdown(pMarkdown, this.log);
	}

	/**
	 * The markdown editor's live preview re-renders the typed text on every keystroke through
	 * pict-section-content, which has no way to escape HTML -- so a remark carrying an `onerror`
	 * attribute ran in the browser of whoever opened it for editing. The preview is off; the View
	 * toggle shows the formatted remark through the hardened renderer above.
	 *
	 * @param {Object} pInput @param {any} pValue @param {Object} pInst
	 * @return {Object} - the markdown editor's view options.
	 */
	_buildEditorOptions(pInput, pValue, pInst)
	{
		return Object.assign(super._buildEditorOptions(pInput, pValue, pInst), { EnableRichPreview: false });
	}

	/**
	 * PSF records every keystroke's content as the instance's `lastValue`, so it is the remark as typed
	 * even after a whole-form marshal has reset the hidden input. See EditorBase._reassertHiddenInputValue.
	 *
	 * @param {Record<string, any>} pInstance
	 * @return {string|undefined}
	 */
	_getOpenEditorValue(pInstance)
	{
		return (pInstance && (typeof pInstance.lastValue === 'string')) ? pInstance.lastValue : undefined;
	}

	/**
	 * Belt-and-braces content seed for the mounted editor.
	 *
	 * PSF's own hand-off (stash the content in AppData, pass the address as `ContentDataAddress`) does
	 * work — verified live: the address resolves and carries the right content. But it only takes
	 * effect if the editor actually built a segment editor, which requires CodeMirror to be on the
	 * page (`window.CodeMirrorModules`). Writing segment 0 directly after render costs nothing and
	 * makes the content independent of that indirection.
	 *
	 * NOTE: this does NOT rescue a missing CodeMirror — with no segment editors the editor renders an
	 * empty slot and this no-ops (setSegmentContent warns "index out of range"). Serving the CodeMirror
	 * bundle is the actual requirement; see the report definition's index.html.
	 *
	 * @param {Object} pView @param {Object} pInput @param {any} pValue @param {Function} fCallback
	 */
	/**
	 * Resolve `window.CodeMirrorModules`, fetching the vendor bundle once if the host has not already
	 * loaded it. The promise is cached so N fields on a page share a single fetch.
	 * @return {Promise<void>}
	 */
	_ensureCodeMirror()
	{
		if ((typeof window === 'undefined') || _codeMirrorLoaded())
		{
			return Promise.resolve();
		}
		if (this._codeMirrorLoadPromise)
		{
			return this._codeMirrorLoadPromise;
		}
		const tmpURL = (this.options && ('CodeMirrorVendorURL' in this.options)) ?
			this.options.CodeMirrorVendorURL : _DEFAULT_CODEMIRROR_URL;
		if (!tmpURL)
		{
			return Promise.resolve();
		}
		this._codeMirrorLoadPromise = new Promise((fResolve) =>
		{
			const tmpScript = document.createElement('script');
			tmpScript.src = tmpURL;
			tmpScript.async = true;
			// Resolve either way — a failed load leaves the editor empty, which the caller already
			// handles, and blocking the mount on a vendor fetch would be worse.
			tmpScript.onload = () => fResolve();
			tmpScript.onerror = () =>
			{
				if (this.log)
				{
					this.log.error(`[Pict-Input-RichText] CodeMirror bundle failed to load from ${tmpURL} — the editor will render empty.`);
				}
				fResolve();
			};
			document.head.appendChild(tmpScript);
		});
		return this._codeMirrorLoadPromise;
	}

	/**
	 * Warm the CodeMirror bundle in the BACKGROUND as soon as a field initializes, so the first click on
	 * Edit does not block on a cold ~1.1MB fetch (the "significant delay on a clean form" — once cached,
	 * later edits are instant, which is why it was not reproducible after the first). The fetch is
	 * promise-cached in `_ensureCodeMirror`, so N fields share one load and this is safe to call per
	 * field. Scheduled on idle (falling back to a short timeout) so it never competes with first paint.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpResult = super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		if ((typeof window !== 'undefined') && !_codeMirrorLoaded())
		{
			const tmpWarm = () =>
			{
				// Fire-and-forget; a failed fetch is already handled where the editor mounts.
				this._ensureCodeMirror();
			};
			if (typeof window.requestIdleCallback === 'function')
			{
				window.requestIdleCallback(tmpWarm, { timeout: 2000 });
			}
			else
			{
				setTimeout(tmpWarm, 0);
			}
		}
		return tmpResult;
	}

	/**
	 * An empty TEST remark prints nothing, rather than an "(empty)" box. Hidden only in Print (the
	 * editor container carries `pict-editor-print` there — on screen the empty state is a hint to add
	 * remarks instead), only when empty, and only for a remarks field attached to a test acceptance
	 * input, so every other RichText field keeps its own empty state in the PDF.
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpResult = super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		this._syncEmptyTestRemarkInPrint(pInput, pValue);
		return tmpResult;
	}

	/** A test remark edit redraws the test results summary, which shows the remark beside the result. */
	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpResult = super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
		if (this._isTestRemarksField(pInput))
		{
			libTestResultsSummary.requestSummaryRefresh(this.pict, pView);
		}
		return tmpResult;
	}

	/** @param {Object} pInput @param {any} pValue */
	_syncEmptyTestRemarkInPrint(pInput, pValue)
	{
		if ((typeof document === 'undefined') || !pInput || !pInput.Macro)
		{
			return;
		}
		const tmpEditor = document.getElementById(`PICT-EDITOR-FOR-${pInput.Macro.RawHTMLID}`);
		if (!tmpEditor || !tmpEditor.classList.contains('pict-editor-print') || !this._isTestRemarksField(pInput))
		{
			return;
		}
		// Same emptiness rule PSF uses to decide on its "(empty)" placeholder.
		const tmpContent = this._resolveContent(pInput, pValue);
		const tmpEmpty = !tmpContent || (String(tmpContent).trim().length < 1);
		const tmpColumn = tmpEditor.closest('.column');
		if (tmpColumn)
		{
			tmpColumn.classList.toggle('is-hidden', tmpEmpty);
		}
	}

	/**
	 * A test remarks field shares its group with a SampleTestAcceptance input. That group only ever holds
	 * the test's hidden selector, its result and its remarks, so this is exact — and it survives a
	 * container form's hash salting, because the pair's group is salted together.
	 * @param {Object} pInput @return {boolean}
	 */
	_isTestRemarksField(pInput)
	{
		const tmpGroup = pInput && pInput.PictForm && pInput.PictForm.Group;
		const tmpManifest = this.pict.views.PictFormMetacontroller && this.pict.views.PictFormMetacontroller.manifestDescription;
		if (!tmpGroup || !tmpManifest || !tmpManifest.Descriptors)
		{
			return false;
		}
		return Object.values(tmpManifest.Descriptors).some((pDescriptor) =>
			((pDescriptor.PictForm || {}).Group === tmpGroup) && ((pDescriptor.PictForm || {}).InputType === 'SampleTestAcceptance'));
	}

	_mountEdit(pView, pInput, pValue, fCallback)
	{
		// CodeMirror must be present BEFORE the editor renders — it reads window.CodeMirrorModules
		// while building its segments, and silently produces nothing when they are missing. The warm-up
		// in onInputInitialize usually has it ready by now; this awaits it only if not.
		if ((typeof window !== 'undefined') && !_codeMirrorLoaded())
		{
			this._ensureCodeMirror().then(() => this._doMountEdit(pView, pInput, pValue, fCallback));
			return;
		}
		return this._doMountEdit(pView, pInput, pValue, fCallback);
	}

	/**
	 * Focus the first markdown segment with the caret at the end of the existing remark.
	 * @param {string} pRawHTMLID
	 */
	_focusEditor(pRawHTMLID)
	{
		const tmpEditorViewClass = _codeMirrorLoaded() ? /** @type {any} */ (globalThis).CodeMirrorModules.EditorView : null;
		const tmpContainer = document.getElementById(this.getEditorContainerID(pRawHTMLID));
		const tmpContent = tmpContainer ? tmpContainer.querySelector('.cm-content') : null;
		const tmpEditorView = (tmpEditorViewClass && tmpContent) ? tmpEditorViewClass.findFromDOM(tmpContent) : null;
		if (!tmpEditorView)
		{
			return;
		}
		tmpEditorView.focus();
		tmpEditorView.dispatch({ selection: { anchor: tmpEditorView.state.doc.length }, scrollIntoView: true });
	}

	/**
	 * @param {Object} pView @param {Object} pInput @param {any} pValue @param {Function} fCallback
	 */
	_doMountEdit(pView, pInput, pValue, fCallback)
	{
		const tmpContent = this._resolveContent(pInput, pValue);
		return super._mountEdit(pView, pInput, pValue, (pError) =>
		{
			if (!pError && tmpContent)
			{
				try
				{
					const tmpInstance = this._instances[pInput.Hash];
					const tmpEditorView = tmpInstance && tmpInstance.viewInstance;
					if (tmpEditorView && (typeof tmpEditorView.setSegmentContent === 'function'))
					{
						tmpEditorView.setSegmentContent(0, tmpContent);
					}
				}
				catch (pSeedError)
				{
					if (this.log)
					{
						this.log.warn('[Pict-Input-RichText] could not seed editor content',
							{ error: pSeedError.message, inputHash: pInput && pInput.Hash });
					}
				}
			}
			if (typeof fCallback === 'function')
			{
				fCallback(pError);
			}
		});
	}
}

module.exports = PictProviderInputTypeRichText;

module.exports.default_configuration = _DEFAULT_CONFIGURATION;
